import React from 'lib/react';
import {
  EVGrid, EVTypography, EVButton, EVCard, EVCardContent,
} from 'lib/components';
import { useTranslation } from 'lib/translation';
import { useDispatch, useSelector } from 'lib/redux';
import { PANEL_TYPES } from 'features/order/Order.constants';
import { setPanelType, propertyDataRequest as propertyDataRequestAction } from '../../Order.actions';
import { formatAddress } from '../../Order.utils';
import PropertyDataLoader from '../PropertyDataLoader.component';
import PropertyDataDisplayStyles from './PropertyDataDisplayStyles';
import StructureDataCard from './StructureDataCard.component';
import { getRoofArea } from './PropertyData.utils';
import PropertyDataDisclaimer from './PropertyDataDisclaimer.component';
import PropertyDataImages from './PropertyDataImages.component';

function PropertyDataDisplay() {
  const classes = PropertyDataDisplayStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const {
    propertyDataResult, propertyDataRequest, geocodedAddress,
  } = useSelector(({ order, orderAddresses }) => ({
    propertyDataResult: order.propertyDataResult,
    propertyDataRequest: order.propertyDataRequest,
    geocodedAddress: orderAddresses.geocodedAddress,
  }));

  const handleReset = () => {
    dispatch(setPanelType(PANEL_TYPES.PRE_PANEL));
  };

  const handleNext = () => {
    dispatch(setPanelType(PANEL_TYPES.STRUCTURE_MEASUREMENTS));
  };

  const handleRetry = () => {
    const completeAddress = formatAddress(geocodedAddress);
    dispatch(propertyDataRequestAction(completeAddress));
  };

  const { evImageLatest } = propertyDataResult || {};

  const isPropertyDataLoading = propertyDataRequest.loading || propertyDataResult.loading;

  if (isPropertyDataLoading) {
    return <PropertyDataLoader />;
  }

  if (propertyDataRequest.error || propertyDataResult.error) {
    return (
      <EVGrid className={classes.propertyDataContainer}>
        <EVTypography className={classes.title}>
          {t('order_property_data_title', 'Property Data')}
        </EVTypography>
        <EVTypography className={classes.errorMessage}>
          {t('order_property_data_error', 'Failed to load property data')}
        </EVTypography>
        <EVButton
          variant="outlined"
          color="primary"
          onClick={handleRetry}
          className={classes.backButton}
        >
          {t('order_property_data_retry', 'Retry')}
        </EVButton>
        <EVButton
          variant="outlined"
          color="primary"
          onClick={handleReset}
          className={classes.backButton}
        >
          {t('common__reset')}
        </EVButton>
      </EVGrid>
    );
  }

  const structures = propertyDataResult?.data?.structures || [];
  const imageryData = propertyDataResult?.data?.imagery || {};

  if (structures.length === 0) {
    return (
      <EVGrid className={classes.propertyDataContainer}>
        <EVTypography className={classes.title}>
          {t('order_property_data_title', 'Property Data')}
        </EVTypography>
        <EVTypography className={classes.dataValue}>
          {t('order_property_data_no_data', 'No property data available')}
        </EVTypography>
        <EVButton
          variant="outlined"
          color="primary"
          onClick={handleReset}
          className={classes.backButton}
        >
          {t('common__reset')}
        </EVButton>
      </EVGrid>
    );
  }

  return (
    <EVGrid className={classes.propertyDataContainer}>
      <PropertyDataDisclaimer />
      <EVTypography className={classes.title}>
        {t('order_property_data_title', 'Property Data')}
      </EVTypography>

      <EVCard className={classes.dataCard}>
        <EVCardContent>
          <EVTypography className={classes.dataLabel} style={{ marginBottom: '16px' }}>
            Structure Count
          </EVTypography>
          <EVTypography className={classes.dataValue}>
            {structures.length}
          </EVTypography>
        </EVCardContent>
      </EVCard>

      {/* Property Images Section */}
      {(Object.keys(imageryData).length > 0 || evImageLatest) && (
        <EVCard className={classes.dataCard}>
          <EVCardContent>
            <EVTypography className={classes.dataLabel} style={{ marginBottom: '16px' }}>
              Property Images
            </EVTypography>

            <PropertyDataImages
              imageryData={imageryData}
              evImageLatest={evImageLatest}
            />
          </EVCardContent>
        </EVCard>
      )}

      {structures
        .sort((a, b) => getRoofArea(b) - getRoofArea(a))
        .map((structure, index) => (
          <StructureDataCard key={`${index + 1}`} structure={structure} index={index} />
        ))}

      <EVGrid container justifyContent="space-between">
        <EVButton
          variant="outlined"
          color="primary"
          onClick={handleReset}
        >
          {t('common__reset')}
        </EVButton>
        <EVButton
          variant="contained"
          color="primary"
          onClick={handleNext}
        >
          {t('common_next')}
        </EVButton>
      </EVGrid>
    </EVGrid>
  );
}

export default PropertyDataDisplay;
